import { FocusKeyManager } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { MatLegacyDialog as MatDialog, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { blob2Base64asDataURL } from 'cloud-shared-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CuttedMaterialPanel } from '../../models/cutting/cutting-cutted-material-panel.model';

import { PanelsSchemaService } from '../../services/panels-schema.service';
import { PanelsSchemaDialogComponent } from '../panels-schema-dialog/panels-schema-dialog.component';
import { FocusableElementComponent } from './focusable-element/focusable-element.component';

@Component({
  selector: 'csl-panels-schema',
  templateUrl: './panels-schema.component.html',
  styleUrls: ['./panels-schema.component.scss'],
})
export class PanelsSchemaComponent implements OnInit, AfterViewInit, OnDestroy {
  curretntSchemaSafed: SafeResourceUrl;
  panels: CuttedMaterialPanel[] = [];
  materialId = 0;
  keyManager: FocusKeyManager<FocusableElementComponent>;
  showProgressBar = false;
  panelsSchemaDialogRef: MatDialogRef<PanelsSchemaDialogComponent>;
  selectedPanel: CuttedMaterialPanel;
  unsubscribe$: Subject<void> = new Subject();

  @Input() set data(val: { materialId: number; panels: CuttedMaterialPanel[]; selectedPanelUuid: string }) {
    this.panels = val.panels;
    this.materialId = val.materialId;

    if (val.selectedPanelUuid) {
      this.selectedPanel = this.panels.find(p => p.uuid === val.selectedPanelUuid);
    }
  }

  @Input() linkToSchemas = '';
  @Input() fullscreen = false;

  @ViewChildren(FocusableElementComponent, { emitDistinctChangesOnly: false })
  items: QueryList<FocusableElementComponent>;

  constructor(
    private domSanitizer: DomSanitizer, //
    public dialog: MatDialog,
    private panelsSchemaService: PanelsSchemaService,
    private ref: ChangeDetectorRef
  ) {}

  ngOnInit(): void {}

  ngAfterViewInit(): void {
    this.keyManager = new FocusKeyManager<FocusableElementComponent>(this.items).withWrap();

    this.keyManager.change.pipe(takeUntil(this.unsubscribe$)).subscribe(() => {
      if (this.keyManager.activeItem) {
        this.getPanelSchema(this.keyManager.activeItem.item.uuid).then(
          () => (this.selectedPanel = this.keyManager.activeItem.item)
        );
      }
    });

    if (this.selectedPanel) {
      this.setActive(this.panels.findIndex(p => p.uuid === this.selectedPanel.uuid));
    }

    // only after selected panel checking
    this.keyManager.setFirstItemActive();
  }

  openPanelSchemaDialog(): void {
    this.panelsSchemaDialogRef = this.dialog.open(PanelsSchemaDialogComponent, {
      disableClose: false,
      panelClass: 'panels-schema-dialog',
    });

    this.panelsSchemaDialogRef.componentInstance.linkToSchemas = this.linkToSchemas;
    this.panelsSchemaDialogRef.componentInstance.data = {
      materialId: this.materialId,
      panels: this.panels,
      selectedPanelUuid: this.selectedPanel ? this.selectedPanel.uuid : undefined,
    };

    this.panelsSchemaDialogRef.afterClosed().subscribe(() => {
      this.panelsSchemaDialogRef = null;
    });
  }

  setActive(index: number): void {
    if (index === this.keyManager.activeItemIndex) {
      this.keyManager.updateActiveItem(-1);
    }

    this.keyManager.setActiveItem(index);
  }

  async getPanelSchema(panelUUID: string): Promise<void> {
    this.showProgressBar = true;
    this.ref.detectChanges();
    try {
      let file: Blob = await this.panelsSchemaService.getPanelSchema(this.materialId, panelUUID, this.linkToSchemas);
      file = file.slice(0, file.size, "image/svg+xml")
      const curretntSchema = await blob2Base64asDataURL(file);
      this.curretntSchemaSafed = this.domSanitizer.bypassSecurityTrustResourceUrl(curretntSchema);
      this.showProgressBar = false;
      this.ref.detectChanges();
    } catch (err) {
      this.curretntSchemaSafed = undefined;
      this.showProgressBar = false;
      this.ref.detectChanges();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }
}
